<template>
    <div class="bf-grid-container">
        <div class="bf-grid" :style="labelPosition === 'left' ? { '--bf-label-width': labelWidth + 'px' } : {}">
            <div
                v-for="field in visibleFields"
                :key="field.key"
                :class="['bf-cell', { 'is-repeater': field.type === 'repeater' }]"
                :style="fieldWidthStyle(field)"
            >
                <!-- The caption a conditional block announces: a row of its own,
                     so the fields under it start where every other row does. -->
                <template v-if="field.type === 'caption'">
                    <p class="bf-band-caption">
                        <span class="bf-band-dot" aria-hidden="true"></span>
                        {{ $t('Conditional fields') }} · {{ field.caption }}
                    </p>
                </template>

                <!-- Grouped fields — multiple inputs under one label -->
                <template v-else-if="field.type === 'group'">
                    <!-- If any child has a custom edit slot, delegate the whole row to that slot -->
                    <template v-if="editing && groupAnchorSlotName(field)">
                        <slot :name="groupAnchorSlotName(field)" :field="field" :label-position="labelPosition" />
                    </template>
                    <b-field v-else :horizontal="labelPosition === 'left'">
                        <template #label><field-label :field="field" :editing="editing" /></template>
                        <template v-if="!editing">
                            <div class="is-flex" style="gap: 0.5rem; flex-wrap: wrap">
                                <template v-if="filledGroupChildren(field).length">
                                    <span v-for="child in filledGroupChildren(field)" :key="child.key" class="bf-value">
                                        {{ formatDisplay(child) }}
                                    </span>
                                </template>
                                <span v-else class="bf-value has-text-grey">—</span>
                            </div>
                        </template>
                        <div v-else class="bf-group-edit">
                            <template v-for="child in field.children" :key="child.key">
                                <b-checkbox
                                    v-if="child.type === 'boolean'"
                                    :model-value="
                                        modelValue[child.key] === '1' ||
                                        modelValue[child.key] === true ||
                                        modelValue[child.key] === 1
                                    "
                                    @update:model-value="updateValue(child.key, $event ? '1' : '0')"
                                >
                                    {{ child.label }}
                                </b-checkbox>
                                <b-select
                                    v-else-if="child.type === 'select'"
                                    :model-value="modelValue[child.key]"
                                    @update:model-value="updateValue(child.key, $event)"
                                    expanded
                                >
                                    <option value="">—</option>
                                    <option v-for="opt in child.options || []" :key="opt" :value="opt">
                                        {{ opt }}
                                    </option>
                                </b-select>
                                <app-datepicker
                                    v-else-if="child.type === 'date'"
                                    :model-value="toDateValue(modelValue[child.key])"
                                    @update:model-value="updateValue(child.key, $event ? formatDate($event) : null)"
                                />
                                <b-input
                                    v-else
                                    :model-value="modelValue[child.key]"
                                    @update:model-value="updateValue(child.key, $event)"
                                    :type="htmlInputType(child.type)"
                                    :placeholder="child.label"
                                    expanded
                                />
                            </template>
                        </div>
                    </b-field>
                </template>

                <!-- Repeater group (email, phone, website) — all values stacked under
                     one label, each row keeping its type label, link and copy button -->
                <template v-else-if="field.type === 'repeater-group'">
                    <b-field :horizontal="labelPosition === 'left'">
                        <template #label>
                            <field-label :field="field" />
                        </template>
                        <div class="bf-value">
                            <p v-for="item in field.items" :key="item.key" class="bf-repeater-row bf-copy-cell">
                                <span v-if="item.icon" class="bf-repeater-icon" :title="item.typeLabel">
                                    <b-icon :icon="item.icon" size="is-small" />
                                </span>
                                <a
                                    v-if="item.href"
                                    :href="item.href"
                                    class="bf-link"
                                    @click="onLinkClick(item, $event)"
                                >
                                    {{ item.value }}
                                </a>
                                <span v-else>{{ item.value }}</span>
                                <span v-if="item.extension" class="bf-repeater-extension">
                                    {{ $t('Ext.') }} {{ item.extension }}
                                </span>
                                <span v-if="item.isPrimary" class="bf-repeater-primary" :title="$t('Primary')">
                                    <b-icon icon="star" size="is-small" />
                                </span>
                                <a
                                    class="bf-copy"
                                    :class="{ 'is-copied': copiedKey === item.key }"
                                    :title="copiedKey === item.key ? $t('Copied!') : $t('Copy to clipboard')"
                                    @click.stop.prevent="copyToClipboard(item.value, item.key)"
                                >
                                    <b-icon :icon="copiedKey === item.key ? 'check' : 'content-copy'" size="is-small" />
                                </a>
                            </p>
                            <span v-if="!field.items.length" class="has-text-grey">—</span>
                        </div>
                    </b-field>
                </template>

                <!-- The slot follows the branch: a read-only field renders its
                     value while editing, so it wants the `display-` override, not
                     the `edit-` one. Keyed on `editing` alone, a fiche that had
                     formatted a date in its display slot printed the raw stored
                     string the moment the pencil was clicked. -->
                <slot
                    v-else
                    :name="(editing && !field.readOnly ? 'edit-' : 'display-') + field.key"
                    :value="editing && !field.readOnly ? modelValue[field.key] : field.value"
                    :field="field"
                    :update="v => updateValue(field.key, v)"
                    :label-position="labelPosition"
                >
                    <b-field
                        :horizontal="labelPosition === 'left'"
                        :type="errors[field.key] ? 'is-danger' : null"
                        :message="errors[field.key] || null"
                    >
                        <!-- A checkbox and a switch print their own name beside
                             themselves, which is what makes them read as a
                             sentence. Repeated above, the same words appear
                             twice in a row and a card of five switches becomes
                             ten lines saying five things — so the wrapper steps
                             aside and the control keeps the label, asterisk and
                             help tooltip that were here. -->
                        <template v-if="!namesItself(field)" #label>
                            <field-label :field="field" :editing="editing" />
                        </template>
                        <!-- DISPLAY MODE — also while editing, for a field the
                             record writes itself. -->
                        <template v-if="!editing || field.readOnly">
                            <!-- A control that names itself shows itself, ticked
                                 or not and out of reach. Rendered as "Non" beside
                                 a label instead, the card changed shape under the
                                 pencil — and a row whose only content was the word
                                 "Non" read as a blank where the switch had been. -->
                            <choice-input
                                v-if="namesItself(field)"
                                :field="field"
                                :model-value="field.value"
                                :disabled="true"
                            />
                            <!-- Rich text is rendered, not printed: the value is
                                 the editor's own HTML, and a paragraph of it
                                 escaped reads as `<p>…</p>` to the person who
                                 wrote a sentence. Long ones collapse, because a
                                 description is sometimes a page. -->
                            <comment-body
                                v-else-if="field.type === 'richtext'"
                                :body="field.value || ''"
                                :max-height="220"
                            />
                            <p v-else class="bf-value">
                                <!-- `link`: on a fiche the name of another record
                                     is a dead end otherwise — the reader has to
                                     leave and search for it. A list cell says no,
                                     where the row click already owns the tap. -->
                                <relation-value v-if="field.relation" :entity="field.type" :value="field.value" link />
                                <template v-else>{{ formatDisplay(field) }}</template>
                            </p>
                        </template>

                        <!-- EDIT MODE -->
                        <template v-else>
                            <!-- Phone — the same control a contact's phones use, so
                                 a number typed on any fiche is masked as it is
                                 typed and stored in one shape.

                                 The `has-addons` wrapper is not decoration: the
                                 component renders the country button and the
                                 number as two sibling controls, and without it
                                 Bulma stacks them one above the other. -->
                            <div v-if="field.type === 'phone'" class="field has-addons mb-0">
                                <app-phone-input
                                    :model-value="modelValue[field.key] || ''"
                                    @update:model-value="updateValue(field.key, $event)"
                                    :placeholder="field.placeholder || field.label"
                                    v-bind="field.inputProps"
                                />
                            </div>

                            <!-- Text / Email / URL -->
                            <b-input
                                v-else-if="isTextType(field.type)"
                                :model-value="modelValue[field.key]"
                                @update:model-value="updateValue(field.key, $event)"
                                :type="htmlInputType(field.type)"
                                :placeholder="field.placeholder || field.label"
                                v-bind="field.inputProps"
                            />

                            <!-- Number -->
                            <b-input
                                v-else-if="field.type === 'number'"
                                :model-value="modelValue[field.key]"
                                @update:model-value="updateValue(field.key, $event)"
                                type="number"
                                step="any"
                                :placeholder="field.placeholder || field.label"
                                v-bind="field.inputProps"
                            />

                            <!-- Duration — typed as « 2h30 » or « 1j », bound as minutes -->
                            <duration-input
                                v-else-if="field.type === 'duration'"
                                :model-value="durationMinutes(modelValue[field.key])"
                                @update:model-value="updateValue(field.key, $event)"
                                :placeholder="field.placeholder || ''"
                                v-bind="field.inputProps"
                            />

                            <!-- Money -->
                            <b-input
                                v-else-if="field.type === 'money'"
                                :model-value="modelValue[field.key]"
                                @update:model-value="updateValue(field.key, $event)"
                                type="number"
                                step="0.01"
                                min="0"
                                :placeholder="field.placeholder || field.label"
                                v-bind="field.inputProps"
                            />

                            <!-- Textarea -->
                            <b-input
                                v-else-if="field.type === 'textarea'"
                                :model-value="modelValue[field.key]"
                                @update:model-value="updateValue(field.key, $event)"
                                type="textarea"
                                rows="2"
                                v-bind="field.inputProps"
                            />

                            <!-- Rich text. The column holds the HTML the editor
                                 produces, so a textarea here would show the
                                 tags and flatten them on the next save. A
                                 built-in type only: offering it for a field an
                                 administrator creates would mean accepting
                                 markup from anywhere, which is a different
                                 question from rendering our own editor's. -->
                            <rich-text-editor
                                v-else-if="field.type === 'richtext'"
                                :model-value="modelValue[field.key]"
                                @update:model-value="updateValue(field.key, $event)"
                                :placeholder="field.label"
                                framed
                                auto-height
                                default-expanded
                            />

                            <!-- Single choice — select, radio or boolean. Which
                                 control it renders with is the placement's
                                 `widget`; what it stores never changes. -->
                            <choice-input
                                v-else-if="isChoiceField(field)"
                                :field="field"
                                :model-value="modelValue[field.key]"
                                @update:model-value="updateValue(field.key, $event)"
                            />

                            <!-- Multiple choice — some of a closed list -->
                            <multi-choice-input
                                v-else-if="field.type === 'multiselect'"
                                :model-value="modelValue[field.key]"
                                :options="field.options || []"
                                :placeholder="field.placeholder"
                                @update:model-value="updateValue(field.key, $event)"
                            />

                            <!-- Checkbox (multi-choice) -->
                            <div v-else-if="field.type === 'checkbox'">
                                <b-checkbox
                                    v-for="opt in field.options || []"
                                    :key="opt"
                                    :native-value="opt"
                                    :model-value="toArray(modelValue[field.key])"
                                    @update:model-value="updateValue(field.key, $event.join(', '))"
                                >
                                    {{ opt }}
                                </b-checkbox>
                            </div>

                            <!-- Date -->
                            <app-datepicker
                                v-else-if="field.type === 'date'"
                                :model-value="toDateValue(modelValue[field.key])"
                                @update:model-value="updateValue(field.key, $event ? formatDate($event) : null)"
                                v-bind="field.inputProps"
                            />

                            <!-- User relation ('employee' kept for builtin fields that predate the User/Employee split) -->
                            <employee-autocomplete
                                v-else-if="field.type === 'user' || field.type === 'employee'"
                                :model-value="modelValue[field.key] || null"
                                @update:model-value="updateValue(field.key, $event)"
                                :placeholder="field.placeholder || $t('Select one')"
                            />

                            <!-- Employee relation → HR/payroll EmployeeProfile record -->
                            <employee-autocomplete
                                v-else-if="field.type === 'employee_profile'"
                                source-url="/employee-profiles"
                                :model-value="modelValue[field.key] || null"
                                @update:model-value="updateValue(field.key, $event)"
                                :placeholder="field.placeholder || $t('Select one')"
                            />

                            <!-- Contact relation -->
                            <app-contact
                                v-else-if="field.type === 'contact'"
                                :horizontal="false"
                                :selected-id="modelValue[field.key] || null"
                                :placeholder="field.placeholder || $t('Select one')"
                                :filters="field.relationFilters"
                                @select="opt => updateValue(field.key, opt?.id ?? null)"
                            />

                            <!-- File -->
                            <b-upload
                                v-else-if="field.type === 'file'"
                                @input="updateValue(field.key, $event?.name || '')"
                            >
                                <a class="button is-light">
                                    <b-icon icon="upload" size="is-small"></b-icon>
                                    <span>{{ modelValue[field.key] || $t('Choose a file') }}</span>
                                </a>
                            </b-upload>
                        </template>
                    </b-field>
                </slot>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, getCurrentInstance, onBeforeUnmount, ref, useSlots } from 'vue';
import { useDateFormat } from '@/composables/useDateFormat';
import RelationValue from '@/components/base/RelationValue.vue';
import { fieldWidthStyle } from '@/utils/layoutWidth';
import ChoiceInput from '@/components/base/ChoiceInput.vue';
import MultiChoiceInput from '@/components/base/MultiChoiceInput.vue';
import { choiceEntries } from '@/utils/choiceEntries';
import CommentBody from '@/components/CommentBody.vue';
import { labelsItself } from '@/utils/choiceWidget';
import { formatDuration, formatHours } from '@/utils/duration';

/**
 * Repeater links (email/phone/website) follow their href by default. A field may
 * opt into custom behaviour instead by carrying an `onClick` handler — used by
 * LayoutSectionRenderer to open the in-app email composer rather than mailto:.
 */
function onLinkClick(field, event) {
    if (typeof field.onClick !== 'function') return;
    event.preventDefault();
    field.onClick();
}

// Copy-to-clipboard on repeater rows (email/phone/website): the checkmark
// replaces the copy icon for 2s. Keyed per row so only the copied one shows
// the success state.
const copiedKey = ref(null);
let copiedTimer = null;

async function copyToClipboard(value, key) {
    if (!value) {
        return;
    }

    try {
        await navigator.clipboard.writeText(value);
    } catch {
        // Fallback for browsers/contexts without the async clipboard API
        const input = document.createElement('textarea');
        input.value = value;
        input.setAttribute('readonly', '');
        input.style.position = 'absolute';
        input.style.left = '-9999px';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
    }

    copiedKey.value = key;
    clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => {
        copiedKey.value = null;
    }, 2000);
}

onBeforeUnmount(() => {
    clearTimeout(copiedTimer);
});

const props = defineProps({
    fields: {
        type: Array,
        required: true
    },
    modelValue: {
        type: Object,
        default: () => ({})
    },
    editing: {
        type: Boolean,
        default: false
    },
    /**
     * Validation messages keyed by field key — the shape of a 422 error bag, so
     * a rule checked in the browser and one checked by the server land on the
     * same field, the same way.
     */
    errors: {
        type: Object,
        default: () => ({})
    },
    labelPosition: {
        type: String,
        default: 'top' // 'top' | 'left'
    },
    labelWidth: {
        type: Number,
        default: 160
    }
});

const emit = defineEmits(['update:modelValue']);

const { formatDate, toDateValue } = useDateFormat();
const { proxy } = getCurrentInstance();
const $filters = proxy.$filters;
const slots = useSlots();

function groupAnchorSlotName(field) {
    for (const child of field.children || []) {
        const name = 'edit-' + child.key;
        if (slots[name]) return name;
    }
    return null;
}

const visibleFields = computed(() => {
    return props.fields.filter(f => f.visible !== false);
});

// `phone` stays in the list: it is a text type for everything except the edit
// control, which is app-phone-input.
const textTypes = ['text', 'email', 'url', 'phone'];

function isTextType(type) {
    return textTypes.includes(type);
}

function htmlInputType(type) {
    if (type === 'email') return 'email';
    if (type === 'url') return 'url';
    if (type === 'phone') return 'tel';
    return 'text';
}

function toArray(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return String(value)
        .split(',')
        .map(v => v.trim())
        .filter(Boolean);
}

// Empty children (e.g. a missing salutation) are hidden instead of rendering
// a stray "—" next to the filled values; "—" only shows when all are empty.
function filledGroupChildren(field) {
    return (field.children || []).filter(child => {
        const value = child.value;
        return value !== null && value !== undefined && value !== '';
    });
}

/**
 * Minutes as the duration field binds them: a number, or null.
 *
 * A record loaded from the API carries « 150 » as a string often enough — a
 * decimal column, a CSV, a payload built by hand — and the field's prop is
 * typed `Number`, so Vue would warn and the grammar would read nothing.
 */
function durationMinutes(value) {
    if (value === null || value === undefined || value === '') return null;

    const minutes = Number(value);

    return Number.isFinite(minutes) ? minutes : null;
}

function formatDisplay(field) {
    const value = field.value;

    if (value === null || value === undefined || value === '') return '—';

    switch (field.type) {
        case 'money':
            return $filters?.money(value) ?? value;
        // Stored E.164, read back the way it is written.
        case 'phone':
            return $filters?.phone(value) ?? value;
        case 'boolean':
            return value === 1 || value === '1' || value === true || value === 'true' ? i18n.t('Yes') : i18n.t('No');
        case 'checkbox':
            return Array.isArray(value) ? value.join(', ') : value;
        case 'multiselect':
            return choiceEntries(value).join(', ') || '—';
        // Dates were printing their stored ISO string, so every fiche that
        // wanted one readable wrote a slot to format it. Formatted here, a date
        // declared in the catalogue reads properly wherever it is placed.
        case 'date':
            return $filters?.shortdate(value) ?? value;
        case 'datetime':
            return $filters?.datetime(value) ?? value;
        // Minutes are stored and a duration is read: « 150 » is a number
        // somebody has to divide in their head before it means a working
        // afternoon. Both go through the house grammar (utils/duration), but
        // not through the same rendering — `duration` is a *planned* length and
        // may group into days, `hours` is a *measured* one and never does. Eight
        // hours logged against a task read « 8 h »; the estimate they are
        // compared with may read « 1 j ».
        case 'hours': {
            const minutes = Number(value);

            if (!Number.isFinite(minutes) || minutes === 0) return '—';

            return formatHours(minutes);
        }
        case 'duration': {
            const minutes = Number(value);

            if (!Number.isFinite(minutes) || minutes <= 0) return '—';

            return formatDuration(minutes);
        }
        default:
            return value;
    }
}

/** Types that hold exactly one value out of a declared list. */
function isChoiceField(field) {
    return field.type === 'select' || field.type === 'radio' || field.type === 'boolean';
}

/**
 * Whether the control this field renders with already carries its name.
 *
 * True in both modes, because the fiche shows the same control either way: a
 * switch that is off looks off, and reading it takes no words at all. A control
 * that names only its options — a dropdown, a radio group — keeps the label
 * above it in both modes, or a bare "Non" would sit there answering nothing.
 */
function namesItself(field) {
    return isChoiceField(field) && labelsItself(field);
}

function updateValue(key, value) {
    emit('update:modelValue', { ...props.modelValue, [key]: value });
}
</script>

<style scoped>
/* Two containers, two names, because two rules ask different questions.
   `bf-grid` is how wide the card is — it decides whether a quarter is still
   readable. `bf-cell` is how wide one field is — it decides whether its label
   still fits beside it. Unnamed, a cell would answer the card's question and a
   half-width band would collapse its own fields to full width. */
.bf-grid-container {
    container-type: inline-size;
    container-name: bf-grid;
}

.bf-grid {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 0.75rem 1.5rem;
}

.bf-cell {
    grid-column: span var(--layout-span, 12);
    min-width: 0;
    container-type: inline-size;
    container-name: bf-cell;
}

/* A label to the left needs room for itself AND a value. Once the cell is too
   narrow to hold both — a sixth of a section, or a half of a phone-width pane —
   the label goes back on top instead of squeezing the value to one character
   per line. The section still says `left`; this is the cell refusing an
   arrangement that cannot be read. */
@container bf-cell (max-width: 250px) {
    .bf-cell :deep(.field.is-horizontal) {
        display: block;
    }

    .bf-cell :deep(.field.is-horizontal > .field-label) {
        width: auto;
        min-width: 0;
        margin-right: 0;
        padding-top: 0;
        text-align: left;
    }

    .bf-cell :deep(.field.is-horizontal .field-label .label::after) {
        content: none;
    }
}

/* Container queries, not viewport ones: a section is as wide as the panel
   holding it, which on a master-detail pane has nothing to do with the screen.

   Tuned for a column, not a page: a card's column is already a fraction of it,
   so a grid of ~400px is the normal case rather than a cramped one. Each step
   lands while the field it produces is still wide enough to type into.

   Each step lands while the field it produces is still well past 120px — a half
   of 480, then the whole row under 380. */
@container bf-grid (max-width: 480px) {
    .bf-cell {
        grid-column: span var(--layout-span-md, 12);
    }
}

@container bf-grid (max-width: 380px) {
    .bf-cell {
        grid-column: span var(--layout-span-sm, 12);
    }

    /* Once every cell takes the whole row, a column gap separates nothing — and
       it is not free. The gap sits between all twelve tracks, so eleven of them
       come to 264px whatever the grid's width: in a column narrower than that
       the tracks collapse to zero and every cell is wider than the grid holding
       it. That is how a value in Informations système reached 32px into the
       column beside it — the width of one gap, spilling out of a 205px column. */
    .bf-grid {
        column-gap: 0;
    }
}

/* Two columns before anything inside a row has to fold. A repeater carrying a
   work number is six controls wide — type, country, number, extension, star,
   bin — and needs about 330px; three of them across a 1050px card are 330 each,
   so that is where a third stops being one. Below it they pair up: two halves of
   an 816px card are 396px, which holds the whole row on one line.

   `max()` so a repeater the org declared wider keeps its width — this rule only
   ever widens. The plain declaration above it is what a browser without math in
   a grid span falls back to: today's layout, unchanged. */
@container bf-grid (max-width: 1050px) {
    .bf-cell.is-repeater {
        grid-column: span var(--layout-span, 12);
        grid-column: span max(var(--layout-span, 12), 6);
    }
}

/* A repeater is a row of controls — a type picker, the value, a star and a bin —
   not a single input, so it stops being typable at about twice the width a
   plain field does. A third of a full-width card on a tablet is 172px, and the
   value input had what the four others left it. Given the whole row it is
   usable at any width the card itself survives.

   Its own rule rather than a lower step for everyone: the identity card's rows
   are designed as three thirds, and moving the general ladder to rescue the
   repeaters would break those on an ordinary desktop. */
@container bf-grid (max-width: 700px) {
    .bf-cell.is-repeater {
        grid-column: span 12;
    }
}

.bf-band-caption {
    /* A row of its own: the fields it announces start where every other row
       starts, so an always-present field beside them lines up. */
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    /* The whole caption carries the colour, dot included: it is what tells the
       reader at a glance that these fields come and go.

       `-on-scheme` rather than the raw amber, which measures 1.75:1 on the light
       scheme — invisible. This derivation reads as the same colour and clears AA
       in both themes (7.1:1 light, 10.4:1 dark). Its mirror `--bulma-warning-dark`
       is the opposite trap: 16.5:1 light, 1.1:1 dark. */
    color: var(--bulma-warning-on-scheme);
    margin-bottom: 0;
}

.bf-band-dot {
    width: 0.45rem;
    height: 0.45rem;
    border-radius: 50%;
    background: currentColor;
    flex: none;
}

/* Labels quiet, values full strength.
   Side by side, position said which was which. Stacked — which is how this
   fiche reads — the two lines are the same size and weight, and with the same
   colour the value reads as a second label. In edit mode the input's own
   contrast drew that line for free, so only the read view lost it. */
.bf-grid :deep(.label),
.bf-grid :deep(.field-label-inline) {
    color: var(--bulma-text-weak);
}

.bf-value {
    padding: 0.35em 0;
    line-height: 1.5;
    word-break: break-word;
    color: var(--bulma-text-strong);
}

.bf-group-edit {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem 1rem;
    padding-top: 0.35em;
}

.bf-link {
    display: inline-flex;
    align-items: center;
}

/* Repeater group: each value on its own line under a single label, prefixed by
   a type icon (email/phone, or the network's icon for websites). The type name
   is exposed as the icon's tooltip. */
.bf-repeater-row {
    padding: 0.1em 0;
}
.bf-repeater-icon {
    flex: 0 0 auto;
    display: inline-flex;
    color: var(--bulma-text-weak);
}
.bf-repeater-primary {
    flex: 0 0 auto;
    display: inline-flex;
    color: var(--bulma-warning);
}

/* The extension trails the number rather than competing with it: it is how to
   finish the call, not another number to read. */
.bf-repeater-extension {
    flex: 0 0 auto;
    color: var(--bulma-text-weak);
}

/* Repeater row: the copy icon only appears while hovering the row (or right
   after a successful copy). Mirrors the contact list copy affordance. */
.bf-copy-cell {
    display: flex;
    align-items: center;
    gap: 0.35rem;
}
.bf-copy {
    opacity: 0;
    flex: 0 0 auto;
    line-height: 1;
    color: var(--bulma-text-weak);
    transition:
        opacity 0.15s ease,
        color 0.15s ease;
}
.bf-copy-cell:hover .bf-copy,
.bf-copy.is-copied {
    opacity: 1;
}
.bf-copy:hover {
    color: var(--bulma-primary);
}
.bf-copy.is-copied {
    color: var(--bulma-success);
}

/* Horizontal label width override */
.bf-grid :deep(.field.is-horizontal .field-label) {
    width: var(--bf-label-width, 160px);
    flex: none;
    min-width: var(--bf-label-width, 160px);
    padding-top: 0.35em;
}

/* Remove default bottom margin on label so it aligns with the value baseline */
.bf-grid :deep(.field.is-horizontal .field-label .label) {
    margin-bottom: 0;
    line-height: 1.5;
    text-align: left;
}
</style>
